import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { apolloClient } from '@client/api/graphql/client';
import toast from 'react-hot-toast';
import {
  DeletePostDocument,
  DeletePostMutation,
  DeletePostMutationVariables,
  Post,
} from '../../../../generated/graphql';
import { communityQueryKeys } from '../constants';

export const useDeleteCommunityPost = (communityId: number) => {
  const queryClient = useQueryClient();
  const postsKey = communityQueryKeys.posts(+communityId);
  const pinnedKey = communityQueryKeys.pinnedPosts(+communityId);

  const deleteFromList = (postId: string) =>
    queryClient.setQueryData<InfiniteData<Post[]> | undefined>(
      postsKey,
      (previousData) => {
        if (previousData?.pages && Array.isArray(previousData.pages)) {
          return {
            ...previousData,
            pages: previousData.pages.map((page) =>
              page.filter((post) => post.id !== postId),
            ),
          };
        }
        return previousData;
      },
    );

  const deleteFromPinned = (postId: string) =>
    queryClient.setQueryData<Post[] | undefined>(pinnedKey, (previousData) =>
      Array.isArray(previousData)
        ? previousData.filter((post) => post.id !== postId)
        : previousData,
    );

  return useMutation<unknown, unknown, string, VoidFunction>(
    (postId) =>
      apolloClient.mutate<DeletePostMutation, DeletePostMutationVariables>({
        mutation: DeletePostDocument,
        variables: { communityId: +communityId, postId },
      }),
    {
      onMutate: async (postId) => {
        await Promise.all([
          queryClient.cancelQueries({ queryKey: postsKey }),
          queryClient.cancelQueries({ queryKey: pinnedKey }),
        ]);
        const previousPosts = queryClient.getQueryData(postsKey);
        const previousPinned = queryClient.getQueryData(pinnedKey);
        deleteFromList(postId);
        deleteFromPinned(postId);
        return function rollback() {
          queryClient.setQueryData(postsKey, () => previousPosts);
          queryClient.setQueryData(pinnedKey, () => previousPinned);
        };
      },
      onSuccess: (_data, postId, rollback) => {
        rollback?.();
        deleteFromList(postId);
        deleteFromPinned(postId);
        queryClient.invalidateQueries({ queryKey: postsKey });
        queryClient.invalidateQueries({ queryKey: pinnedKey });
        toast.success('Your post was successfully deleted!');
      },
      onError: (_error, _variables, rollback) => {
        rollback?.();
        toast.error('Something went wrong');
      },
    },
  );
};
